import * as RadixTabs from '@radix-ui/react-tabs'
import { forwardRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

interface TabItem {
  label: string
  content: React.ReactNode
}

interface TabsProps {
  items: TabItem[]
  defaultValue?: string
}

const MotionTabPanel = forwardRef<HTMLDivElement, React.ComponentProps<typeof motion.div>>(
  (props, ref) => <motion.div ref={ref} {...props} />,
)
MotionTabPanel.displayName = 'MotionTabPanel'

export const Tabs: React.FC<TabsProps> = ({ items, defaultValue }) => {
  const [tab, setTab] = useState(defaultValue || (items[0] ? `tab-${0}` : ''))
  const [prevIdx, setPrevIdx] = useState<number>(0)

  const currentIdx = items.findIndex((_, idx) => `tab-${idx}` === tab)

  const handleTabChange = (nextTab: string) => {
    setPrevIdx(currentIdx)
    setTab(nextTab)
  }

  return (
    <RadixTabs.Root value={tab} onValueChange={handleTabChange} className="w-full">
      <RadixTabs.List className="flex border-brand-navy gap-16 py-4">
        {items.map((item, idx) => (
          <RadixTabs.Trigger
            key={idx}
            value={`tab-${idx}`}
            className={
              'group relative flex items-center px-3 py-2 font-bold text-base text-text-secondary/40 transition-colors focus:outline-none bg-transparent border-0 hover:text-brand-blue cursor-pointer data-[state=active]:text-brand-blue'
            }
          >
            <div
              className={
                'absolute left-0 top-1/2 -translate-y-1/2 h-8 w-1.5 rounded-full transition-all duration-200 ' +
                'bg-text-secondary/40 group-data-[state=active]:bg-brand-blue'
              }
              aria-hidden="true"
            />
            <span className="pl-4">{item.label}</span>
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      <div className="relative">
        <AnimatePresence mode="wait" initial={false}>
          {items.map((item, idx) => {
            if (tab !== `tab-${idx}`) return null
            const direction = idx > prevIdx ? 1 : -1
            return (
              <RadixTabs.Content asChild forceMount value={`tab-${idx}`} key={idx}>
                <MotionTabPanel
                  key={tab}
                  initial={{ opacity: 0, x: 16 * direction }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 16 * direction }}
                  transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
                  className="w-full"
                >
                  {item.content}
                </MotionTabPanel>
              </RadixTabs.Content>
            )
          })}
        </AnimatePresence>
      </div>
    </RadixTabs.Root>
  )
}

export default Tabs
